/**
 * THE DE-IDENTIFIED SUMMARY OF A CLIENT FILE (owner decision, 1 Oct 2026).
 *
 * The platform owner wants to understand the people the service helps — which goals they come with,
 * and the broad shape of their wealth and spending — without anyone's file being opened. This is the
 * whole of what leaves a fact-find for that purpose: a household type, an age band, which goals are
 * ticked, whether they own their home, and BANDS (never amounts) for home value, super, net wealth and
 * living expenses. No name, date of birth, contact detail, location or exact figure.
 *
 * It is computed on every save, written beside the encrypted file, and kept ONLY for clients advised
 * under our own licence — the database function that stores it refuses anything else (migration
 * 0031). Reports suppress any group smaller than ten people, so a band cannot be traced to a person.
 *
 * Bands are deliberately coarse: widen them rather than narrow them. A change here changes what the
 * Privacy Policy describes, so read section 5 of it before adding a field.
 */
import type { FactFind, FactFindAsset, FactFindGoals, FactFindLiability } from '../calculation/factFindTypes.ts';

export const INSIGHT_GOALS = [
    'home_care',
    'downsize_home',
    'retirement_village',
    'granny_flat',
    'aged_care',
    'meet_costs',
    'maximise_support',
    'preserve_assets',
    'funding_guidance',
] as const satisfies readonly (keyof FactFindGoals)[];
export type InsightGoal = (typeof INSIGHT_GOALS)[number];

/** Upper bounds (exclusive) and labels. The last band is open-ended. */
const WEALTH_BANDS: readonly [number, string][] = [
    [250_000, 'under $250k'],
    [500_000, '$250k–$500k'],
    [750_000, '$500k–$750k'],
    [1_000_000, '$750k–$1m'],
    [1_500_000, '$1m–$1.5m'],
    [2_000_000, '$1.5m–$2m'],
    [3_000_000, '$2m–$3m'],
    [Infinity, '$3m and over'],
];
const SUPER_BANDS: readonly [number, string][] = [
    [100_000, 'under $100k'],
    [250_000, '$100k–$250k'],
    [500_000, '$250k–$500k'],
    [1_000_000, '$500k–$1m'],
    [Infinity, '$1m and over'],
];
const EXPENSE_BANDS: readonly [number, string][] = [
    [40_000, 'under $40k a year'],
    [60_000, '$40k–$60k a year'],
    [80_000, '$60k–$80k a year'],
    [100_000, '$80k–$100k a year'],
    [150_000, '$100k–$150k a year'],
    [Infinity, '$150k a year and over'],
];
const AGE_BANDS: readonly [number, string][] = [
    [65, 'under 65'],
    [70, '65–69'],
    [75, '70–74'],
    [80, '75–79'],
    [85, '80–84'],
    [90, '85–89'],
    [Infinity, '90 and over'],
];

/** Every band label, in order, so reports can list empty bands too. */
export const INSIGHT_BAND_ORDER = {
    wealth: WEALTH_BANDS.map(([, l]) => l),
    super: SUPER_BANDS.map(([, l]) => l),
    expenses: EXPENSE_BANDS.map(([, l]) => l),
    age: AGE_BANDS.map(([, l]) => l),
} as const;

export interface ClientInsights {
    household: 'single' | 'couple';
    /** The client's (first applicant's) age band at the time of the save; null if no birth date. */
    ageBand: string | null;
    goals: InsightGoal[];
    homeOwner: boolean;
    homeValueBand: string | null;
    /** Superannuation plus account-based pensions, both applicants. */
    superBand: string | null;
    /** Home plus every asset entered, less every debt. */
    netWealthBand: string | null;
    livingExpensesBand: string | null;
}

const band = (value: number, bands: readonly [number, string][]): string =>
    bands.find(([upper]) => value < upper)?.[1] ?? bands[bands.length - 1]![1];

const amountOf = (row: FactFindAsset | FactFindLiability): number => {
    const v = Number(row.value ?? row.balance ?? 0);
    return Number.isFinite(v) && v > 0 ? v : 0;
};

const sumRows = (rows: readonly (FactFindAsset | FactFindLiability)[] | undefined): number =>
    (rows ?? []).reduce((total, r) => total + amountOf(r), 0);

function ageOn(dob: string | undefined, today: Date): number | null {
    if (!dob) return null;
    const born = new Date(dob);
    if (Number.isNaN(born.getTime())) return null;
    let age = today.getFullYear() - born.getFullYear();
    const m = today.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age -= 1;
    return age >= 0 && age < 130 ? age : null;
}

export function clientInsightsOf(factFind: FactFind | null | undefined, today: Date = new Date()): ClientInsights {
    const fd = factFind ?? {};
    const goals = INSIGHT_GOALS.filter((g) => fd.goals?.[g] === true);

    const homeValue = Number(fd.home?.value ?? 0);
    const ownsHome = fd.client?.currentResidency === 'own_home' || (Number.isFinite(homeValue) && homeValue > 0);

    const assets = Object.values(fd.assets ?? {}).reduce((total, rows) => total + sumRows(rows), 0);
    const debts = Object.values(fd.liabilities ?? {}).reduce((total, rows) => total + sumRows(rows), 0);
    const superTotal = sumRows(fd.assets?.superannuation) + sumRows(fd.assets?.accountBasedPensions);
    const homePart = Number.isFinite(homeValue) && homeValue > 0 ? homeValue : 0;
    const anyWealthEntered = homePart > 0 || assets > 0 || debts > 0;
    const netWealth = homePart + assets - debts;

    const living = Number(fd.expenses?.livingExpenses ?? 0);
    const age = ageOn(fd.client?.dob, today);

    return {
        household: fd.adviceFor === 'couple' ? 'couple' : 'single',
        ageBand: age === null ? null : band(age, AGE_BANDS),
        goals,
        homeOwner: ownsHome,
        homeValueBand: homePart > 0 ? band(homePart, WEALTH_BANDS) : null,
        superBand: superTotal > 0 ? band(superTotal, SUPER_BANDS) : null,
        // A negative net wealth is a real answer (debts exceed assets) and falls in the lowest band.
        netWealthBand: anyWealthEntered ? band(Math.max(0, netWealth), WEALTH_BANDS) : null,
        livingExpensesBand: Number.isFinite(living) && living > 0 ? band(living, EXPENSE_BANDS) : null,
    };
}
